import { useCallback, useContext, useMemo } from 'react';
import * as Sentry from '@sentry/react';
// import { useLoginWithDevoCredsMutation } from '@matrix-forge/graphql-client';
import { gql, useMutation } from '@apollo/client';
import { makeUserInfoFromLoginResponse } from '@/utils/userInfo';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { publicClient } from '@/utils/apollo';
import { shouldRelaunchInNewWindow, relaunchInNewWindow } from '@/utils/win';
import { initSentry } from '@/sentry';

/**
 * This convenience hook combines useLoginWithDevoCredsMutation hook with the UserInfoContext. Unlike that hook,
 * this hook takes an object rather than a JSON string. It stringifies the object, passes it to the
 * hook, and then after the mutation succeeds, it transforms the response into a UserInfo object and
 * stores it in the UserInfoContext.
 */
export const useLoginWithDevoMetadata = () => {
  const { setUserInfo, setUserMetadata } = useContext(UserInfoContext);
  // @workaround useLoginWithDevoCredsMutation typing is not working with publicClient, so use useMutation instead
  // TODO: fix this, maybe use codegen to generate a more compatible version of publicClient
  // const [login, loginResult] = useLoginWithDevoCredsMutation({ client: publicClient });
  const [login, loginResult] = useMutation(
    gql`
      mutation LoginWithDevoCreds($details: Json!) {
        loginWithDevoCreds(details: $details) {
          details
          features {
            gitlabToolsEnabled
            customAgents
            secretsManagementEnabled
          }
        }
      }
    `,
    { client: publicClient }
  );

  // Wrapper around the login mutation trigger. Takes an object rather than a JSON string.
  // After mutation succeeds:
  // - Transforms the mutation response into a UserInfo object and stores it in UserInfoContext.
  const loginWithMetadata = useCallback(
    (metadata: object) => {
      const metadataJSON = JSON.stringify(metadata);
      login({ variables: { details: metadataJSON } }).then(
        res => {
          try {
            const loginResponse = res.data?.loginWithDevoCreds;
            let nextUserInfo = loginResponse?.details
              ? makeUserInfoFromLoginResponse(JSON.parse(loginResponse.details))
              : null;

            // Add features from the mutation response if available
            if (nextUserInfo && loginResponse?.features) {
              nextUserInfo = {
                ...nextUserInfo,
                features: loginResponse.features,
              };
            }

            setUserInfo(nextUserInfo);
            setUserMetadata(metadataJSON);
            if (shouldRelaunchInNewWindow()) {
              // Don't bother initializing sentry here, do it in the new window.
              relaunchInNewWindow();
            } else {
              // We are in the new window, initialize sentry now (except for development).
              if (!window.location.origin.includes('localhost')) {
                // Disable sentry masking for internal users & environments.
                const unmask =
                  nextUserInfo?.email?.includes('@devo.com') ||
                  window.location.origin.includes('devo.engineering') ||
                  document.referrer.includes('us.poc.devo.com');
                initSentry(!!unmask);
              }
            }
          } catch (err) {
            console.error('UserInfoProvider: error parsing login response', err);
            initSentry(false);
            Sentry.captureException(err, {
              tags: {
                errorType: 'mutation',
                mutationType: 'loginWithMetadata',
                component: 'useLoginWithDevoMetadata',
              },
              extra: {
                mutationVariables: { details: metadataJSON },
              },
            });
          }
        },
        err => {
          initSentry(false);
          Sentry.captureException(err, {
            tags: {
              errorType: 'mutation',
              mutationType: 'loginWithDevoCreds',
              component: 'useLoginWithDevoMetadata',
            },
            extra: {
              mutationVariables: { details: metadataJSON },
            },
          });
        }
      );
    },
    [login, setUserInfo, setUserMetadata]
  );

  return useMemo(
    () => [loginWithMetadata, loginResult] as [typeof login, typeof loginResult],
    [loginWithMetadata, loginResult]
  );
};
